import React from 'react';
import { CreditCard, Wallet, Home, ShoppingBag, AlertCircle, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import SelectOption from '../ui/SelectOption';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

const LOAN_PURPOSES = [
  { value: 'debt_consolidation', icon: Wallet },
  { value: 'credit_card_refi', icon: CreditCard },
  { value: 'emergency', icon: AlertCircle },
  { value: 'home_improvement', icon: Home },
  { value: 'large_purchases', icon: ShoppingBag },
  { value: 'other', icon: HelpCircle },
] as const;

type LoanPurposeFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function LoanPurposeForm({ formData, onSubmit }: LoanPurposeFormProps) {
  const { t } = useTranslation('wizard');
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  return (
    <div id="loan-purpose-form" className="space-y-6">
      <div className="text-center">
        <Title 
          id="loan-purpose-title"
          name="loan-purpose-title"
          as="h1" 
          className="mb-2"
        >
          {t('loanPurpose.title')}
        </Title>
        <Description 
          id="loan-purpose-description"
          name="loan-purpose-description"
          size="lg"
        >
          {t('loanPurpose.description')}
        </Description>
      </div>

      <div className="grid gap-3">
        {LOAN_PURPOSES.map(({ value, icon }) => (
          <SelectOption
            key={value}
            id={`loan-purpose-option-${value}`}
            name={`loan-purpose-option-${value}`}
            icon={icon}
            label={t(`loanPurpose.options.${value}.label`)}
            description={t(`loanPurpose.options.${value}.description`)}
            onClick={() => onSubmit({ loanPurpose: value })}
          />
        ))}
      </div>

      {showDisclaimer && (
        <Disclaimer
          id="loan-purpose-disclaimer"
          name="loan-purpose-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}
    </div>
  );
}